import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getPackageByIdRequest,
  updatePackageRequest,
} from "../../../../Backend/SchoolsRequests";
import TableTemplate from "../../../../Components/Table/TableTemplate";
import { SCHOOL_RETURNS_BY_STUDENT } from "../../../../Constants/Endpoints";
import { bookNumberMask, displayDate } from "../../../../Helpers/utils/utils";
import { hasValue } from "../../../../Helpers/validations/common";
import { BookScanModal } from "../BookScanModal/BookScanModal";
import { PackageStatusCell } from "../../../../Components/PackageStatusCell/PackageStatusCell";
import Switch from "../../../../Components/Switch/Switch";
import "./PackageReturns.scss";

export function PackageReturns() {
  const tableTemplateRef = useRef(null);
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { schoolId, packageId, year } = useParams();
  const userData = useSelector((state) => state.user.credentials);
  const [scanMode, setScanMode] = useState(true);
  const [highlightValues, setHighlightValues] = useState([]);
  const [highlightKey] = useState("id");

  const [barcodeInfo, setBarcodeInfo] = useState({
    barcode: "",
    classNumber: null,
    completeDate: null,
    id: "",
    isCompleted: false,
    schoolScanComplete: false,
    schoolScanUser: "",
    userId: "",
    year: 0,
    student: {
      id: "",
      currentBarcode: null,
      userName: "",
      firstName: "",
      preposition: "",
      lastName: "",
      email: "",
      softEmail: "",
      postalCode: "",
      adress: "",
      country: null,
      houseNumber: "",
      houseNumberAddition: "",
      city: "",
      returnedId: "",
      extraProperties: null,
      showAdvice: false,
      isEnabled: false,
      havePendingSchools: false,
      isExternalUser: false,
      role: null,
      roleNames: [],
      customerId: null,
      schoolName: "",
      schoolNumber: null,
      schoolId: null,
    },
  });

  const [columns] = useState([
    {
      name: "book.bookNumber",
      headerCaption: t("BookNumber"),
      caption: (x) => bookNumberMask(x.book.bookNumber),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "book.title",
      headerCaption: t("Title"),
      size: "big",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "book.isbn",
      headerCaption: t("ISBN"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "schoolScan",
      headerCaption: t("Delivered"),
      size: "small",
      canSort: false,
      canFilter: false,
      canEdit: false,
      textAlign: "center",
      caption: (x) => (
        <span className="is-checkbox">
          {x.schoolScan ? "check_box" : "check_box_outline_blank"}
        </span>
      ),
    },
    {
      name: "schoolSuitable",
      headerCaption: t("BookCondition"),
      canSort: false,
      canFilter: false,
      canEdit: false,
      textAlign: "center",
      caption: (x) =>
        x.schoolScan ? (
          <span className="suitable-column">
            {x.schoolSuitable ? t("Suitable") : t("NotSuitable")}
          </span>
        ) : (
          "-"
        ),
    },
  ]);

  useEffect(() => {
    getPackageById();
  }, []);

  const getPackageById = async () => {
    const resp = await getPackageByIdRequest(schoolId, packageId, year);
    if (resp.success) {
      setBarcodeInfo(resp.data.list[0]);
    }
  };

  const handleFinalize = async () => {
    const body = [
      {
        id: packageId,
        schoolScanComplete: true,
        schoolScanUser: userData.id,
      },
    ];
    const resp = await updatePackageRequest(body);
    if (resp.success) {
      getPackageById();
      tableTemplateRef.current.getItems();
    }
  };

  const handleEdit = async () => {
    const body = [
      {
        id: packageId,
        schoolScanComplete: false,
        schoolScanUser: userData.id,
      },
    ];
    const resp = await updatePackageRequest(body);
    if (resp.success) {
      getPackageById();
      tableTemplateRef.current.getItems();
    }
  };

  return (
    <div className="package-returns-page">
      <div className="header-page-buttons-wrapper">
        <div
          className="goback"
          onClick={() => navigate(-1)}
          role="link"
          id={"goback-button"}
        >
          <div className="icon">arrow_back</div>
          <div className="text">{t("GoBack")}</div>
        </div>
        <div className={"right-wrapper"}>
          {barcodeInfo.schoolScanComplete && (
            <div className="completed">
              <PackageStatusCell status={barcodeInfo.schoolScanComplete} />
              <div className="date">
                {t("Completed") +
                  ": " +
                  displayDate(barcodeInfo.schoolScanCompleteDate)}
              </div>
            </div>
          )}
          {barcodeInfo.schoolScanComplete ? (
            <div className="button-save-wrapper">
              <Button className="button-save" onClick={() => handleEdit()}>
                {t("Edit")}
              </Button>
            </div>
          ) : (
            <div className="button-save-wrapper">
              <Button className="button-save" onClick={() => handleFinalize()}>
                {t("Finalize")}
              </Button>
            </div>
          )}
          {barcodeInfo.schoolScanComplete === false && (
            <Switch
              label={t("ScanMode")}
              checked={scanMode}
              onChange={setScanMode}
            />
          )}
        </div>
      </div>
      <div className={"main-return-wrapper"}>
        {hasValue(barcodeInfo.id) && (
          <div
            className={`${
              barcodeInfo.schoolScanComplete === false && scanMode ? "grid-return-wrapper side" : "grid-return-wrapper"
            }`}
          >
            <TableTemplate
              title={`${barcodeInfo.student.firstName} ${barcodeInfo.student.preposition} ${barcodeInfo.student.lastName}`}
              highlightValues={highlightValues}
              highlightKey={highlightKey}
              subtitle={`${barcodeInfo.barcode}`}
              ref={tableTemplateRef}
              endpoint={
                SCHOOL_RETURNS_BY_STUDENT +
                "?barcodeId=" +
                packageId +
                "&sortProp=schoolScanDate&descending=true"
              }
              columns={columns}
              uniqueId="id"
              paginator={true}
            />
          </div>
        )}
        {hasValue(barcodeInfo.id) && (
          <div
            className={`${
              barcodeInfo.schoolScanComplete === false && scanMode
                ? "book-return-side-wrapper side"
                : "book-return-side-wrapper"
            }`}
          >
            <BookScanModal
              onSubmit={(id) => {
                tableTemplateRef.current.getItems();
                setHighlightValues([id]);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
